import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {ObjectBaseComponent} from '../../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../../core/shared/object/service/observer.service';
import {NavigationItem} from '../../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../../core/shared/widget/service/widget.factory';
import {buildCwRequestTypePartnerWidget} from '../../../../widget/cw-request-type-partner.widget';
import {buildCwRequestTypeDocumentRoleWidget} from '../../../../widget/cw-request-type-document-role.widget';
import {buildCwRequestTypeTicketTypeWidget} from '../../../../widget/cw-request-type-ticket-type.widget';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: [T3 · P1] Object component of cw.CwRequestType. The entity has THREE one-to-many children
 *   and is therefore a COMPLEX CONFIGURATION ENTITY — the mandated shape (CONFIG-003) is a left
 *   navigation with one section per child, NOT an inline master-detail in the config page.
 *   Reactivates the three previously unused child widget builders unchanged.
 */
@Component({
    selector: 'cw-request-type-object-component',
    templateUrl: './cw-request-type-object.component.html',
    styleUrls: ['./cw-request-type-object.component.scss'],
    standalone: false
})
export class CwRequestTypeObjectComponent extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    /**
     * MANDATORY left navigation (one section per 1:n child plus the head data).
     * An ObjectComponent of a complex configuration entity WITHOUT left navigation is an
     * architectural error (hierarchical-config-pattern.md, Anti-Pattern 4).
     */
    navigationItems: NavigationItem[] = [
        {
            label: 'Übersicht',
            action: 'overview',
            tooltip: 'Stammdaten des Antragstyps',
            icon: 'fa-solid fa-circle-info',
            toggleable: false,
            default: true
        },
        {
            label: 'Gesellschaften',
            action: 'partners',
            tooltip: 'Zulässige Gesellschaften dieses Antragstyps',
            icon: 'fa-solid fa-building-columns',
            toggleable: false,
            default: false
        },
        {
            label: 'Dokumentrollen',
            action: 'documentRoles',
            tooltip: 'Erwartete Dokumentrollen dieses Antragstyps',
            icon: 'fa-solid fa-file-lines',
            toggleable: false,
            default: false
        },
        {
            label: 'Tickettypen',
            action: 'ticketTypes',
            tooltip: 'Tickettypen dieses Antragstyps',
            icon: 'fa-solid fa-ticket',
            toggleable: false,
            default: false
        }
    ];

    /** Head: read view of the intake type's base data. */
    requestTypeDataWidget: WidgetData;
    /** Head: metadata-driven edit form, built lazily on the first "Bearbeiten" toggle. */
    requestTypeEditWidget: WidgetData;
    editRequestType: boolean = false;

    /** The three child catalogues, each filtered by this intake type. */
    partnerWidget: WidgetData;
    documentRoleWidget: WidgetData;
    ticketTypeWidget: WidgetData;

    private queryParamSubscription: Subscription;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService,
        private route: ActivatedRoute) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.subscribeToNavItemQueryParam();
    }

    /**
     * Called once the CwRequestType DTO has loaded — the base class contract delivers the DTO here,
     * never in ngOnInit. Publishes the left navigation and (re)builds every scoped widget.
     */
    onObjectChanged(): void {
        this.setPageTitle(`${this.dto?.['name'] ?? 'Antragstyp'} – Antragseingang`);

        if (!this.activeNavigationItems) {
            this.activeNavigationItems = this.navigationItems[0];
        }
        this.onNavigationItems.emit(this.navigationItems);

        this.editRequestType = false;
        this.requestTypeEditWidget = null;

        this.buildRequestTypeDataWidget();
        this.buildChildWidgets();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    override ngOnDestroy(): void {
        this.queryParamSubscription?.unsubscribe();
        super.ngOnDestroy();
    }

    private get requestTypeId(): number {
        return this.objectIdentifier.objectId;
    }

    /** Supports deep links of the form {@code cw/cwRequestType/:id?navItem=partners}. */
    private subscribeToNavItemQueryParam(): void {
        this.queryParamSubscription = this.route.queryParams.subscribe(params => {
            const navItem = params['navItem'];
            if (!navItem) {
                return;
            }
            const found = this.navigationItems.find(item => item.action === navItem);
            if (found) {
                this.activeNavigationItems = found;
            }
        });
    }

    private buildRequestTypeDataWidget(): void {
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: CwRequestType head read widget (Antragstyp-Stammdaten). Own alias — the flat table
         *   of the config page keeps 'config.cw.CwRequestType.widget' as its SINGLE producer.
         */
        this.requestTypeDataWidget = WidgetFactory.createWidgetEntityData(
            'config.cw.CwRequestType.widget.data',
            'Antragstyp-Stammdaten',
            'cw.CwRequestType',
            this.requestTypeId
        );
    }

    /**
     * Toggles the head between the read data widget and the metadata-driven object form
     * (mvs-crud-object via the object widget) — never a hand-built Angular form.
     */
    toggleEditRequestType(): void {
        this.editRequestType = !this.editRequestType;
        if (this.editRequestType && !this.requestTypeEditWidget) {
            /**
             * AI:
             * Status: "in progress"
             * Type: Widget
             * SubType: Runtime
             * Reason: CwRequestType editable object form (Antragstyp-Stammdaten, edit toggle).
             */
            this.requestTypeEditWidget = WidgetFactory.createWidgetObject(
                'config.cw.CwRequestType.widget.object.edit',
                'Antragstyp bearbeiten',
                'cw.CwRequestType',
                this.requestTypeId
            );
        }
    }

    /**
     * Builds the three child catalogues from their EXISTING builders, unchanged: each already
     * filters on {@code e.requestTypeId} and pre-sets the parent FK on create (CONFIG-012).
     */
    private buildChildWidgets(): void {
        this.partnerWidget = buildCwRequestTypePartnerWidget(this.requestTypeId);
        this.documentRoleWidget = buildCwRequestTypeDocumentRoleWidget(this.requestTypeId);
        this.ticketTypeWidget = buildCwRequestTypeTicketTypeWidget(this.requestTypeId);
    }

}
